import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ShareLinkPreview } from '@kaba/shared';
import { api } from '../lib/api';
import { signIn, usePublicConfig } from '../lib/auth';
import { passkeysSupported, signInWithOidc, signInWithPasskey } from '../lib/sso';
import { queryClient } from '../lib/board';
import { navigate, useLocation } from '../lib/router';
import { Button } from './ui/ui';
import { PasswordInput } from './ui/PasswordInput';
import { input, Shell, translate } from './LoginPage';

const RIGHTS = {
  viewer: 'ansehen',
  commenter: 'ansehen und kommentieren',
  editor: 'ansehen, Karten anlegen und bearbeiten',
} as const;

/** Ziel eines Gastlinks: /g/<token> */
export function GuestPage() {
  const { pathname } = useLocation();
  const token = decodeURIComponent(pathname.slice(3));
  const info = useQuery({
    queryKey: ['share', token],
    queryFn: () => api.get<ShareLinkPreview>(`/api/share/${encodeURIComponent(token)}`),
    retry: false,
  });
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'guest' | 'account'>('guest');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { data: cfg } = usePublicConfig();

  const open = async (guestName = '') => {
    const res = await api.post<{ boardId: string }>(`/api/share/${encodeURIComponent(token)}/join`, { name: guestName });
    await queryClient.invalidateQueries();
    navigate(`/b/${res.boardId}`, { replace: true });
  };
  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(translate((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  // Schon Mitglied: direkt ins Board
  const memberOf = info.data?.member ? info.data.boardId : null;
  useEffect(() => {
    if (memberOf) navigate(`/b/${memberOf}`, { replace: true });
  }, [memberOf]);

  if (info.isError)
    return (
      <Shell title="Gastzugang">
        <p className="text-sm">{info.error.message}</p>
        <Button variant="primary" onClick={() => navigate('/')}>
          Zur Startseite
        </Button>
      </Shell>
    );
  if (!info.data || memberOf) return <Shell title="Gastzugang">Lade …</Shell>;
  const p = info.data;
  const needsName = p.role !== 'viewer';
  const errorBox = error && (
    <p role="alert" className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
      {error}
    </p>
  );
  const intro = (
    <p className="text-sm">
      Du kannst das Board <strong>„{p.boardTitle}“</strong> {RIGHTS[p.role]}.
    </p>
  );

  if (p.me)
    return (
      <Shell title="Gastzugang" brand={p.brand}>
        {intro}
        <p className="text-sm text-[var(--ink-soft)]">
          {p.me.isGuest ? `Du bist als Gast „${p.me.name}“ unterwegs.` : `Angemeldet als ${p.me.name}.`} Das Board erscheint danach in deiner
          Übersicht.
        </p>
        <Button variant="primary" className="min-h-12" disabled={busy} onClick={() => run(() => open())}>
          Board öffnen
        </Button>
        {errorBox}
      </Shell>
    );

  return (
    <Shell title="Gastzugang" brand={p.brand}>
      {intro}
      {mode === 'guest' ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => open(name.trim()));
          }}
        >
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {needsName ? 'Dein Name' : 'Dein Name (freiwillig)'}
            <input
              className={input}
              autoComplete="name"
              maxLength={60}
              required={needsName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={needsName ? 'Vor- und Nachname' : 'Gast'}
              autoFocus
            />
          </label>
          <p className="text-xs text-[var(--ink-soft)]">
            {needsName ? 'Die anderen sehen deinen Namen bei deinen Änderungen und Kommentaren. ' : ''}
            Kein Konto, keine E-Mail nötig. Dieses Gerät bleibt angemeldet.
          </p>
          {errorBox}
          <Button type="submit" variant="primary" className="min-h-12" disabled={busy || (needsName && !name.trim())}>
            Board öffnen
          </Button>
          <button type="button" className="min-h-11 text-sm text-accent-fg underline-offset-2 hover:underline" onClick={() => (setError(null), setMode('account'))}>
            Ich habe ein Konto – anmelden
          </button>
        </form>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              await signIn(email.trim(), password);
              await open();
            });
          }}
        >
          {cfg?.login?.oidc && (
            <Button type="button" variant="primary" className="min-h-12" disabled={busy} onClick={() => run(() => signInWithOidc(location.pathname))}>
              {cfg.login.oidc.label}
            </Button>
          )}
          {cfg?.login?.passkeys && passkeysSupported() && (
            <Button
              type="button"
              className="min-h-12"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  if (await signInWithPasskey(false)) await open();
                })
              }
            >
              Mit Passkey anmelden
            </Button>
          )}
          <input className={input} type="email" placeholder="E-Mail" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <PasswordInput className={input} placeholder="Passwort" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          {errorBox}
          <Button type="submit" variant="primary" className="min-h-12" disabled={busy}>
            Anmelden und öffnen
          </Button>
          <button type="button" className="min-h-11 text-sm text-accent-fg underline-offset-2 hover:underline" onClick={() => (setError(null), setMode('guest'))}>
            Ohne Konto als Gast weiter
          </button>
        </form>
      )}
    </Shell>
  );
}
